// NPM Modules
import { writable } from 'svelte/store';
import collect from 'collect.js';
// Models
import SportcastSelection from '@/Models/SportcastSelection.js';
import config from '@/config.js';
import { EventStatus } from '@/Constants/Event.js';
import { SportSelectionStatus } from '@/Constants/SportSelection.js';
import QueryBuilder from '@/Models/Sarala/QueryBuilder.js';
import { replaceOrAddModelInCollectionStore } from '@/Stores/StoreHelpers.js';
import { debouncer, deepMergeWithSpread } from '@/Services/Helpers.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	// eslint-disable-next-line no-unused-vars
	let _store;
	let meta;
	let _pending = [];
	let processing = false;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const debounce = debouncer(500, 5000);

	async function processPending() {
		if (!processing) {
			processing = true;
			const updatesTopoBeProcessed = _pending;
			_pending = [];
			await methods.applyLiabilityUpdates(updatesTopoBeProcessed);
			processing = false;
		}
	}

	// noinspection DuplicatedCode
	const methods = {
		addToPending(items = []) {
			_pending = _pending.concat(items);
			debounce(() => processPending());
		},

		getPagination() {
			return meta && meta.pagination;
		},

		getSportcastSelections(
			sportEvent,
			pagination = { perPage: 100, pageNo: 1 },
			filters = {
				only_with_bets: false,
				not_resulted: false
			}
		) {
			let sportcastSelections = new SportcastSelection();

			const queryBuilder = new QueryBuilder();

			if (filters.only_with_bets) {
				queryBuilder.where('min_bet_count', 1, null);
			}

			if (filters.not_resulted) {
				queryBuilder.where(
					'not_status_id',
					[
						SportSelectionStatus.Win,
						SportSelectionStatus.Lost,
						SportSelectionStatus.Void,
						SportSelectionStatus.Resulted
					].join(','),
					null
				);
			}

			queryBuilder.paginate(pagination.perPage, pagination.pageNo);

			return sportcastSelections
				.makePersistRequest({
					url: `${sportEvent.baseUrl()}/${sportEvent.resourceName()}/${
						sportEvent.id
					}/sportcast_selections${queryBuilder.getQuery()}`,
					method: 'GET'
				})
				.then((result) => {
					meta = result.meta;
					set(collect(result.data));
				});
		},

		getSportcastSelection(selectionId) {
			let sportcastSelections = new SportcastSelection();

			return sportcastSelections
				.makePersistRequest({
					url: `${sportcastSelections.resourceUrl()}${selectionId}`,
					method: 'GET'
				})
				.then((result) => {
					replaceOrAddModelInCollectionStore(result, this);
				});
		},

		getUnresultedSelections(pagination = null) {
			if (!config.SPORT_ENABLED) return;

			let sportcastSelections = new SportcastSelection();
			sportcastSelections = sportcastSelections
				.where('min_bet_count', 1)
				.where('event_status_id', [EventStatus.Closed, EventStatus.MatchOver].join(','))
				.where(
					'not_status_id',
					[
						SportSelectionStatus.Win,
						SportSelectionStatus.Lost,
						SportSelectionStatus.Void,
						SportSelectionStatus.Resulted
					].join(',')
				)
				.with('sport_event')
				.orderByDesc('event_start_time');
			let results = pagination
				? sportcastSelections.paginate(pagination.size, pagination.number)
				: sportcastSelections.get();

			return results.then((result) => {
				meta = result.meta;
				set(collect(result.data));
			});
		},

		updateManualResults(selection, manualResults) {
			if (!config.SPORT_ENABLED) return;

			try {
				return selection.manualResult(manualResults).then((results) => {
					return results;
				});
			} catch (error) {
				return new Promise(() => {
					throw new Error('Unable to update results for sportscast selection');
				});
			}
		},

		async applyLiabilityUpdates(data) {
			if (!data) return;

			let selectionUpdates = collect(data);
			await update((items) => {
				selectionUpdates.each((selectionUpdate) => {
					let selectionToUpdate = items.firstWhere('id', String(selectionUpdate.id));
					if (selectionToUpdate) {
						delete selectionUpdate.id;
						selectionToUpdate = deepMergeWithSpread(selectionToUpdate, selectionUpdate);
						if (selectionUpdate?.liabilities?.bet_count) {
							selectionToUpdate.bet_count = selectionUpdate?.liabilities?.bet_count;
						}
					}
				});

				return items;
			});
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
